import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import TopNavBar from "../components/TopNavBar";
import { useNavigate, useParams } from "react-router-dom";
import { getDevices } from "../api/devices";
import {
  getServicePlanTemplateById,
  getServicePlanTemplates,
} from "../api/servicePlans";
import { getRouteTemplates } from "../api/routeTemplates";
import {
  createDeviceServicePlan,
  getDeviceServicePlanById,
  getDeviceServicePlans,
  replaceDevicePlanTaskOverrides,
  updateDeviceServicePlan,
} from "../api/devicePlans";

const WEEKDAY_OPTIONS = [
  { value: "1", label: "Mon" },
  { value: "2", label: "Tue" },
  { value: "3", label: "Wed" },
  { value: "4", label: "Thu" },
  { value: "5", label: "Fri" },
  { value: "6", label: "Sat" },
  { value: "7", label: "Sun" },
];

const RECURRENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "every_n_days", label: "Every N days" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const TRIGGER_MODE_OPTIONS = [
  { value: "schedule", label: "Schedule" },
  { value: "condition", label: "Condition" },
  { value: "hybrid", label: "Hybrid" },
];

function normalizeDevices(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.devices)) return payload.devices;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

function buildOverrideDraft(override) {
  return {
    id: String(override.pk_id || `${override.task_id}-${Math.random()}`),
    taskId: String(override.task_id || ""),
    recurrenceTypeOverride: override.recurrence_type_override || "",
    intervalNOverride:
      override.interval_n_override != null
        ? String(override.interval_n_override)
        : "",
    dayOfWeekOverride:
      override.day_of_week_override != null
        ? String(override.day_of_week_override)
        : "",
    dayOfMonthOverride:
      override.day_of_month_override != null
        ? String(override.day_of_month_override)
        : "",
    anchorDateOverride: override.anchor_date_override
      ? String(override.anchor_date_override).slice(0, 10)
      : "",
    triggerModeOverride: override.trigger_mode_override || "",
    mandatoryOverride:
      override.mandatory_override == null
        ? ""
        : String(Number(override.mandatory_override)),
    enabledOverride:
      override.enabled_override == null
        ? ""
        : String(Number(override.enabled_override)),
    notes: override.notes || "",
  };
}

function toIntOrNull(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatTaskSummary(task) {
  if (!task) return "Unknown task";

  const recurrenceType = task.recurrence_type || task.recurrenceType || "daily";
  const intervalN = task.interval_n || task.intervalN;
  const dayOfMonth = task.day_of_month || task.dayOfMonth;
  const dayOfWeek = task.day_of_week || task.dayOfWeek;
  const anchorDate = task.anchor_date || task.anchorDate;
  const triggerMode = task.trigger_mode || task.triggerMode || "schedule";
  const mandatory = task.mandatory;
  const active = task.active;

  const recurrenceLabel =
    recurrenceType === "every_n_days"
      ? intervalN
        ? `Every ${intervalN} day(s)`
        : "Every N days"
      : recurrenceType === "weekly"
        ? dayOfWeek
          ? `Weekly (day ${dayOfWeek})`
          : "Weekly"
        : recurrenceType === "monthly"
          ? dayOfMonth
            ? `Monthly (day ${dayOfMonth})`
            : "Monthly"
          : recurrenceType === "daily"
            ? "Daily"
            : recurrenceType;

  const parts = [recurrenceLabel, `Trigger: ${triggerMode}`];
  if (anchorDate) parts.push(`Anchor: ${String(anchorDate).slice(0, 10)}`);
  if (mandatory !== undefined) parts.push(mandatory ? "Mandatory" : "Optional");
  if (active !== undefined) parts.push(active ? "Active" : "Inactive");

  return parts.join(" · ");
}

export default function ConfigureDevicePlanPage() {
  const navigate = useNavigate();
  const { deviceId } = useParams();

  const [device, setDevice] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState("");
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [routeTemplates, setRouteTemplates] = useState([]);
  const [templateTasks, setTemplateTasks] = useState([]);
  const [overrideDraft, setOverrideDraft] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [form, setForm] = useState({
    active: true,
    servicePlanTemplateId: "",
    routeTemplateId: "",
    startsOn: todayDateString(),
    endsOn: "",
    timezone: "Europe/Copenhagen",
  });

  useEffect(() => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);

    Promise.all([
      getDevices(),
      getServicePlanTemplates(),
      getRouteTemplates(),
      getDeviceServicePlans({ deviceId }),
    ])
      .then(
        ([
          devicesData,
          servicePlansData,
          routeTemplatesData,
          devicePlansData,
        ]) => {
          const allDevices = normalizeDevices(devicesData);
          const selectedDevice = allDevices.find(
            (d) => String(d.pk_id) === String(deviceId),
          );

          if (!selectedDevice) {
            setError("Device was not found");
            return;
          }

          setDevice(selectedDevice);
          setServiceTemplates(servicePlansData?.templates || []);
          setRouteTemplates(routeTemplatesData?.templates || []);
          setPlans(
            Array.isArray(devicePlansData?.plans) ? devicePlansData.plans : [],
          );
        },
      )
      .catch((err) =>
        setError(err.message || "Failed to load device assignment"),
      )
      .finally(() => setLoading(false));
  }, [deviceId]);

  const customerServiceTemplates = useMemo(() => {
    if (!device) return serviceTemplates;

    const customerId = Number(device.customer_id || device.customer_pk_id || 0);
    return serviceTemplates.filter((tpl) => {
      const tplCustomerId =
        tpl.customer_id == null ? null : Number(tpl.customer_id);
      return tplCustomerId == null || tplCustomerId === customerId;
    });
  }, [serviceTemplates, device]);

  const filteredRouteTemplates = useMemo(() => {
    if (!device) return routeTemplates;

    const customerId = Number(device.customer_id || device.customer_pk_id || 0);
    return routeTemplates.filter((tpl) => {
      const tplCustomerId =
        tpl.customer_id == null ? null : Number(tpl.customer_id);
      return tplCustomerId == null || tplCustomerId === customerId;
    });
  }, [routeTemplates, device]);

  // One tab per distinct service type found in the customer's templates,
  // so a device can be assigned a separate plan per type (e.g. remote + on-site).
  // Also include any service type the device already has a plan for, even if
  // no template currently matches its customer scope (e.g. the device moved
  // to a different customer/location after the plan was created) - otherwise
  // that plan's tab disappears and it can never be reached to deactivate it.
  const serviceTypeOptions = useMemo(() => {
    const map = new Map();
    customerServiceTemplates.forEach((tpl) => {
      const id = String(tpl.service_type_id || "");
      if (!id || map.has(id)) return;
      map.set(id, {
        id,
        name: tpl.service_type_name || `Service type #${id}`,
      });
    });
    plans.forEach((plan) => {
      const id = String(plan.service_type_id || "");
      if (!id || map.has(id)) return;
      map.set(id, {
        id,
        name: plan.service_type_name || `Service type #${id}`,
      });
    });
    return [...map.values()];
  }, [customerServiceTemplates, plans]);

  useEffect(() => {
    if (!selectedServiceTypeId && serviceTypeOptions.length > 0) {
      setSelectedServiceTypeId(serviceTypeOptions[0].id);
    }
  }, [serviceTypeOptions, selectedServiceTypeId]);

  const existingPlan = useMemo(() => {
    if (!selectedServiceTypeId) return null;
    return (
      plans.find(
        (plan) =>
          String(plan.service_type_id || "") === String(selectedServiceTypeId),
      ) || null
    );
  }, [plans, selectedServiceTypeId]);

  const filteredServiceTemplates = useMemo(() => {
    const base = !selectedServiceTypeId
      ? customerServiceTemplates
      : customerServiceTemplates.filter(
          (tpl) =>
            String(tpl.service_type_id || "") === String(selectedServiceTypeId),
        );

    // Keep the currently assigned template selectable even if it no longer
    // matches the device's customer scope (e.g. device moved to a different
    // customer/location) - otherwise the dropdown renders blank and the
    // existing plan can't be edited/deactivated through this form.
    const existingTemplateId = existingPlan?.service_plan_template_id;
    if (
      existingTemplateId &&
      !base.some((tpl) => String(tpl.pk_id) === String(existingTemplateId))
    ) {
      return [
        {
          pk_id: existingTemplateId,
          template_name: `${existingPlan.service_plan_template_name || `Template #${existingTemplateId}`} (no longer in scope)`,
          service_type_id: existingPlan.service_type_id,
        },
        ...base,
      ];
    }

    return base;
  }, [customerServiceTemplates, selectedServiceTypeId, existingPlan]);

  // Reset the form to the plan for the currently selected service type
  // (or a blank draft if the device has no plan of that type yet).
  useEffect(() => {
    if (existingPlan) {
      setForm({
        active: Number(existingPlan.active) === 1,
        servicePlanTemplateId: String(
          existingPlan.service_plan_template_id || "",
        ),
        routeTemplateId: String(existingPlan.route_template_id || ""),
        startsOn:
          String(existingPlan.starts_on || "").slice(0, 10) ||
          todayDateString(),
        endsOn: existingPlan.ends_on
          ? String(existingPlan.ends_on).slice(0, 10)
          : "",
        timezone: existingPlan.timezone || "Europe/Copenhagen",
      });
    } else {
      setForm({
        active: true,
        servicePlanTemplateId: "",
        routeTemplateId: "",
        startsOn: todayDateString(),
        endsOn: "",
        timezone: "Europe/Copenhagen",
      });
    }
  }, [existingPlan]);

  useEffect(() => {
    const templateId = form.servicePlanTemplateId;
    if (!templateId) {
      setTemplateTasks([]);
      setOverrideDraft([]);
      return;
    }

    getServicePlanTemplateById(templateId)
      .then((data) => {
        const tasks = Array.isArray(data?.template?.tasks)
          ? data.template.tasks
          : [];
        setTemplateTasks(tasks);
        console.log("Service plan template tasks loaded", tasks);
      })
      .catch(() => setTemplateTasks([]));
  }, [form.servicePlanTemplateId]);

  useEffect(() => {
    if (!existingPlan?.pk_id) {
      setOverrideDraft([]);
      return;
    }

    setOverridesLoading(true);
    getDeviceServicePlanById(existingPlan.pk_id)
      .then((data) => {
        const overrides = Array.isArray(data?.plan?.task_overrides)
          ? data.plan.task_overrides
          : [];
        setOverrideDraft(overrides.map(buildOverrideDraft));
      })
      .catch(() => setOverrideDraft([]))
      .finally(() => setOverridesLoading(false));
  }, [existingPlan?.pk_id]);

  const taskOptions = useMemo(() => {
    const map = new Map();
    templateTasks.forEach((task) => {
      const id = String(task.task_id || task.pk_id || "");
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, task.task_name || `Task #${id}`);
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [templateTasks]);

  const taskById = useMemo(() => {
    const map = new Map();
    templateTasks.forEach((task) => {
      const id = String(task.task_id || task.pk_id || "");
      if (id) {
        map.set(id, task);
      }
    });
    return map;
  }, [templateTasks]);

  const updateOverrideRow = (rowId, patch) => {
    setOverrideDraft((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const removeOverrideRow = (rowId) => {
    setOverrideDraft((prev) => prev.filter((row) => row.id !== rowId));
  };

  const addOverrideRow = () => {
    const used = new Set(overrideDraft.map((row) => String(row.taskId || "")));
    const firstUnused = taskOptions.find(
      (option) => !used.has(String(option.id)),
    );

    setOverrideDraft((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        taskId: firstUnused?.id || "",
        recurrenceTypeOverride: "",
        intervalNOverride: "",
        dayOfWeekOverride: "",
        dayOfMonthOverride: "",
        anchorDateOverride: "",
        triggerModeOverride: "",
        mandatoryOverride: "",
        enabledOverride: "",
        notes: "",
      },
    ]);
  };

  const buildOverridePayload = () => {
    return overrideDraft
      .filter((row) => row.taskId)
      .map((row) => {
        const recurrence = row.recurrenceTypeOverride || null;
        return {
          taskId: toIntOrNull(row.taskId),
          recurrenceTypeOverride: recurrence,
          intervalNOverride:
            recurrence === "every_n_days"
              ? toIntOrNull(row.intervalNOverride)
              : null,
          dayOfWeekOverride:
            recurrence === "weekly" ? toIntOrNull(row.dayOfWeekOverride) : null,
          dayOfMonthOverride:
            recurrence === "monthly"
              ? toIntOrNull(row.dayOfMonthOverride)
              : null,
          anchorDateOverride: row.anchorDateOverride || null,
          triggerModeOverride: row.triggerModeOverride || null,
          mandatoryOverride:
            row.mandatoryOverride === "" ? null : Number(row.mandatoryOverride),
          enabledOverride:
            row.enabledOverride === "" ? null : Number(row.enabledOverride),
          notes: String(row.notes || "").trim() || null,
        };
      })
      .filter((row) => {
        const hasConfig =
          row.recurrenceTypeOverride ||
          row.intervalNOverride != null ||
          row.dayOfWeekOverride != null ||
          row.dayOfMonthOverride != null ||
          row.anchorDateOverride ||
          row.triggerModeOverride ||
          row.mandatoryOverride != null ||
          row.enabledOverride != null ||
          row.notes;
        return row.taskId && hasConfig;
      });
  };

  const onSave = async () => {
    if (!device) return;
    if (!form.servicePlanTemplateId) {
      setSaveError("Service plan template is required");
      return;
    }
    if (!form.routeTemplateId) {
      setSaveError("Route template is required");
      return;
    }
    if (!form.startsOn) {
      setSaveError("Start date is required");
      return;
    }

    const payload = {
      deviceId: Number(device.pk_id),
      servicePlanTemplateId: Number(form.servicePlanTemplateId),
      routeTemplateId: Number(form.routeTemplateId),
      startsOn: form.startsOn,
      endsOn: form.endsOn || null,
      timezone: form.timezone || "Europe/Copenhagen",
      active: form.active ? 1 : 0,
    };

    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(null);

      let saved;
      if (existingPlan?.pk_id) {
        const result = await updateDeviceServicePlan(
          existingPlan.pk_id,
          payload,
        );
        saved = result?.plan || null;
      } else {
        const result = await createDeviceServicePlan(payload);
        saved = result?.plan || null;
      }

      if (saved) {
        setPlans((prev) => {
          const withoutSaved = prev.filter((p) => p.pk_id !== saved.pk_id);
          return [...withoutSaved, saved];
        });
      }

      const planId = saved?.pk_id || existingPlan?.pk_id;
      if (planId) {
        const overridesPayload = buildOverridePayload();
        await replaceDevicePlanTaskOverrides(planId, overridesPayload);
      }

      setSaveSuccess("Device plan assignment saved");
    } catch (err) {
      setSaveError(err.message || "Failed to save device plan assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopNavBar heading="Configure Device Plan" />
      <Container maxWidth="md" sx={{ mt: 3, pb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Tooltip title="Back to device plans">
            <IconButton onClick={() => navigate("/configure-device-plans")}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {device?.name || device?.device_name || `Device #${deviceId}`}
          </Typography>

          {device?.rc_device_id && (
            <Chip label={device.rc_device_id} size="small" variant="outlined" />
          )}

          {existingPlan && (
            <Chip
              label={Number(existingPlan.active) === 1 ? "Enabled" : "Disabled"}
              size="small"
              color={Number(existingPlan.active) === 1 ? "success" : "default"}
              variant="outlined"
            />
          )}
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && device && serviceTypeOptions.length > 0 && (
          <Tabs
            value={selectedServiceTypeId}
            onChange={(e, value) => setSelectedServiceTypeId(value)}
            sx={{ mb: 2 }}
          >
            {serviceTypeOptions.map((option) => {
              const hasPlan = plans.some(
                (plan) =>
                  String(plan.service_type_id || "") === String(option.id),
              );
              return (
                <Tab
                  key={option.id}
                  value={option.id}
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{option.name}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={hasPlan ? "Assigned" : "Not assigned"}
                        color={hasPlan ? "success" : "default"}
                      />
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>
        )}

        {!loading && !error && device && (
          <Stack spacing={2}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.active)}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Automatic device plan enabled"
                />

                <TextField
                  select
                  label="Service plan template"
                  value={form.servicePlanTemplateId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      servicePlanTemplateId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Select service plan template</MenuItem>
                  {filteredServiceTemplates.map((tpl) => (
                    <MenuItem key={tpl.pk_id} value={tpl.pk_id}>
                      {tpl.template_name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Route template"
                  value={form.routeTemplateId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      routeTemplateId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Select route template</MenuItem>
                  {filteredRouteTemplates.map((tpl) => (
                    <MenuItem key={tpl.pk_id} value={tpl.pk_id}>
                      {tpl.template_name}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField
                    type="date"
                    label="Starts on"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.startsOn}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        startsOn: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    type="date"
                    label="Ends on (optional)"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.endsOn}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        endsOn: e.target.value,
                      }))
                    }
                  />
                </Stack>

                <TextField
                  label="Timezone"
                  value={form.timezone}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      timezone: e.target.value,
                    }))
                  }
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ alignSelf: "center" }}
                  >
                    Base assignment and overrides are saved together.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={onSave}
                    disabled={saving}
                  >
                    {saving
                      ? "Saving..."
                      : existingPlan
                        ? "Update Assignment"
                        : "Create Assignment"}
                  </Button>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: 16, fontWeight: 700 }}
                  >
                    Task Overrides
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${overrideDraft.length} rows`}
                  />
                  <Box sx={{ ml: "auto" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addOverrideRow}
                    >
                      Add Override
                    </Button>
                  </Box>
                </Box>

                {overridesLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!overridesLoading && overrideDraft.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No task overrides configured.
                  </Typography>
                )}

                {!overridesLoading &&
                  overrideDraft.map((row) => (
                    <Paper
                      key={row.id}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 1.5 }}
                    >
                      <Stack spacing={1.25}>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            Current task details
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {taskById.get(String(row.taskId))?.task_name ||
                              `Task #${row.taskId || "?"}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {formatTaskSummary(
                              taskById.get(String(row.taskId)),
                            )}
                          </Typography>
                        </Box>

                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.25}
                        >
                          <TextField
                            select
                            fullWidth
                            label="Task"
                            value={row.taskId}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                taskId: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">Select task</MenuItem>
                            {taskOptions.map((option) => {
                              const usedByOtherRow = overrideDraft.some(
                                (existing) =>
                                  existing.id !== row.id &&
                                  String(existing.taskId || "") ===
                                    String(option.id),
                              );
                              return (
                                <MenuItem
                                  key={option.id}
                                  value={option.id}
                                  disabled={usedByOtherRow}
                                >
                                  {option.name}
                                </MenuItem>
                              );
                            })}
                          </TextField>

                          <TextField
                            select
                            fullWidth
                            label="Enabled override"
                            value={row.enabledOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                enabledOverride: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            <MenuItem value="1">Enabled</MenuItem>
                            <MenuItem value="0">Disabled</MenuItem>
                          </TextField>

                          {/* <TextField
                            select
                            fullWidth
                            label="Mandatory override"
                            value={row.mandatoryOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                mandatoryOverride: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            <MenuItem value="1">Mandatory</MenuItem>
                            <MenuItem value="0">Optional</MenuItem>
                          </TextField> */}
                        </Stack>

                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.25}
                        >
                          <TextField
                            select
                            fullWidth
                            label="Recurrence override"
                            value={row.recurrenceTypeOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                recurrenceTypeOverride: e.target.value,
                                intervalNOverride:
                                  e.target.value === "every_n_days"
                                    ? row.intervalNOverride
                                    : "",
                                dayOfWeekOverride:
                                  e.target.value === "weekly"
                                    ? row.dayOfWeekOverride
                                    : "",
                                dayOfMonthOverride:
                                  e.target.value === "monthly"
                                    ? row.dayOfMonthOverride
                                    : "",
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            {RECURRENCE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          {row.recurrenceTypeOverride === "every_n_days" && (
                            <TextField
                              type="number"
                              fullWidth
                              label="Interval N"
                              inputProps={{ min: 1 }}
                              value={row.intervalNOverride}
                              onChange={(e) =>
                                updateOverrideRow(row.id, {
                                  intervalNOverride: e.target.value,
                                })
                              }
                            />
                          )}

                          {row.recurrenceTypeOverride === "weekly" && (
                            <TextField
                              select
                              fullWidth
                              label="Weekday"
                              value={row.dayOfWeekOverride}
                              onChange={(e) =>
                                updateOverrideRow(row.id, {
                                  dayOfWeekOverride: e.target.value,
                                })
                              }
                            >
                              <MenuItem value="">Select weekday</MenuItem>
                              {WEEKDAY_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                          {row.recurrenceTypeOverride === "monthly" && (
                            <TextField
                              type="number"
                              fullWidth
                              label="Day of month"
                              inputProps={{ min: 1, max: 31 }}
                              value={row.dayOfMonthOverride}
                              onChange={(e) =>
                                updateOverrideRow(row.id, {
                                  dayOfMonthOverride: e.target.value,
                                })
                              }
                            />
                          )}

                          {/* <TextField
                            select
                            fullWidth
                            label="Trigger override"
                            value={row.triggerModeOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                triggerModeOverride: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">No override</MenuItem>
                            {TRIGGER_MODE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField> */}
                        </Stack>

                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.25}
                        >
                          <TextField
                            type="date"
                            fullWidth
                            label="Anchor date override"
                            InputLabelProps={{ shrink: true }}
                            value={row.anchorDateOverride}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                anchorDateOverride: e.target.value,
                              })
                            }
                          />

                          <TextField
                            fullWidth
                            label="Notes"
                            value={row.notes}
                            onChange={(e) =>
                              updateOverrideRow(row.id, {
                                notes: e.target.value,
                              })
                            }
                          />

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => removeOverrideRow(row.id)}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </>
  );
}
